import { apiCall } from "@/common/api/ApiResult.model";
import { CognniApp, CognniAppSettings, EditionStr } from "@/common/models";
import { groupByObj, isEmpty } from "@/fp";

import axios from "axios";
import { CMS } from "../cms/cms.api";
import { DEFAULT_APPS_SETTINGS } from "./settings.defaults";

// NOTE: this is a temp client workaround
const populateEmptySettings = async (settings: any) => {
  try {
    await axios.put("/extproxy/integrations/ui-settings", {
      uiSettings: {
        ...settings,
      },
    });
  } catch (e) {
    //
  }
};

export enum ONEDRIVE_SCANNER_STATUS {
  SCANNING_REQUESTED = "SCANNING_REQUESTED",
  SCANNING_IN_PROGRESS = "SCANNING_IN_PROGRESS",
  SCANNING_COMPLETED = "SCANNING_COMPLETED",
  ERROR = "ERROR",
}

export interface Integration {
  service: string;
  keys: { [key: string]: string };
  isConnected: boolean;
  isValidForActivation: boolean;
  // onedrive
  status?: ONEDRIVE_SCANNER_STATUS;
}

export const getOrgAppsSettings = apiCall<
  EditionStr,
  {
    raw: Record<string, any>;
    apps: { [key: string]: CognniAppSettings };
    flags: { [key: string]: boolean };
    rollout: { [key: string]: boolean };
    onPrem: { [key: string]: boolean };
  }
>("get-org-settings", async (userEdition: EditionStr) => {
  const res = await axios.get("/extproxy/integrations/ui-settings");
  const settings = {
    flag_detectionAsTab: false,
    ...DEFAULT_APPS_SETTINGS(userEdition),
    ...(res.data.value.uiSettings || {}),
  };
  // ========== TEMP CLIENT WORKAROUND A HEAD OF YOU =========
  if (isEmpty(res.data.value)) {
    populateEmptySettings(settings);
  }
  // ========== END OF TEMP CLIENT WORKAROUND =========
  const onPrem = Object.entries(settings)
    .filter(([key]) => key.startsWith("onPrem_"))
    .reduce((acc, [key, val]) => {
      // @ts-ignore
      acc[key.replace("onPrem_", "")] = val;
      return acc;
    }, {} as { [key: string]: boolean });

  const rollout = Object.entries(settings)
    .filter(([key]) => key.startsWith("rollout_"))
    .reduce((acc, [key, val]) => {
      // @ts-ignore
      acc[key.replace("rollout_", "")] = val;
      return acc;
    }, {} as { [key: string]: boolean });

  const flags = Object.entries(settings)
    .filter(([key]) => key.startsWith("flag_"))
    .reduce((acc, [key, val]) => {
      // @ts-ignore
      acc[key] = val;
      return acc;
    }, {} as { [key: string]: boolean });

  const apps = Object.entries(settings)
    .filter(([key]) => !key.startsWith("flag_"))
    .reduce((acc, [key, val]) => {
      // @ts-ignore
      acc[key] = val;
      return acc;
    }, {} as { [key: string]: CognniAppSettings });

  return {
    success: true,
    value: {
      raw: settings,
      apps,
      flags,
      rollout,
      onPrem,
    },
  };
});

const getIntegrationServices = apiCall<void, { [key: string]: Integration }>(
  "integration.get",
  async () => {
    const res = await axios.get("/extproxy/integrations/status");
    return {
      success: true,
      value: res.data
        .map((t: Integration) => {
          return {
            ...t,
            keys: t.keys || {},
          };
        })
        // @ts-ignore
        .reduce((acc, s) => {
          acc[s.service] = s;
          return acc;
        }, {} as { [key: string]: Integration }),
    };
  }
);

export const getAllApps = apiCall<
  EditionStr,
  {
    raw: Record<string, any>;
    settings: { [key: string]: CognniAppSettings };
    apps: CognniApp[];
    rollout: { [key: string]: boolean };
    onPrem: { [key: string]: boolean };
    flags: { [key: string]: boolean };
  }
  // @ts-ignore
>("get-apps", async (userEdition) => {
  const res = await CMS.get_apps();

  let apps: CognniApp[] = (res.success && res.value) as CognniApp[];

  const addons_children = groupByObj(
    "parent_app_id",
    apps.filter((a) => a.parent_app_id)
  );

  const addons_parents = apps.filter((a) => !a.parent_app_id);

  apps = addons_parents.map((app) => {
    if (app.id in addons_children) {
      app.addons = addons_children[app.id];
    }
    return app;
  });

  const settingsRes = await getOrgAppsSettings(userEdition);
  const integrationsRes = await getIntegrationServices();

  if (settingsRes.success && integrationsRes.success) {
    const { apps: _apps, flags, rollout, onPrem, raw } = settingsRes.value;
    // @ts-ignore
    apps = apps.map((app) => {
      const service = integrationsRes.value[app.id];
      const a = {
        ...app,
        is: { ..._apps[app.id] },
        ...(service ? { setup: { ...(app.setup || {}), ...service } } : {}),
      };

      if (app.setup && app.setup.type === "keys") {
        // @ts-ignore
        a.setup.keys = app.setup.keys.map((val) => {
          val.value = service.keys[val.key];
          return val;
        });
      }

      if (app.addons) {
        // @ts-ignore
        a["addons"] = [
          ...app.addons.map((addon) => {
            const service = integrationsRes.value[addon.id];
            const a = {
              ...addon,
              is: { ..._apps[addon.id] },
              ...(service
                ? { setup: { ...(addon.setup || {}), ...service } }
                : {}),
            };

            if (addon.setup && addon.setup.type === "keys") {
              // @ts-ignore
              a.setup.keys = addon.setup.keys.map((val) => {
                val.value = service.keys[val.key];
                return val;
              });
            }
            return { ...a, enabledByFlag: flags["flag_app_enable_" + a.id] };
          }),
        ];
      }
      return { ...a, enabledByFlag: flags["flag_app_enable_" + a.id] };
    });
    return {
      success: true,
      value: {
        raw,
        apps,
        settings: settingsRes.value,
        flags,
        onPrem,
        rollout,
      },
    };
  }
  return {
    success: true,
    value: {
      apps: [],
      settings: {},
      flags: {},
      rollout: {},
    },
  };
});

export const updateOrgAppsSettings = apiCall<
  { key: string; settings: CognniAppSettings },
  number
>(
  "update-org-settings",
  async (p: { key: string; settings: CognniAppSettings }) => {
    const { key, settings } = p;
    const res = await axios.put("/extproxy/integrations/ui-settings", {
      uiSettings: {
        [key]: settings,
      },
    });
    return {
      success: true,
      value: res.data,
    };
  }
);

export const connectToService = apiCall<
  { keys: { [key: string]: string }; service: string },
  boolean
>("integration.connect", async ({ keys, service }) => {
  await axios.post(`/extproxy/integrations/${service}/keys` as string, {
    ...keys,
  });
  return {
    success: true,
    value: true,
  };
});

export const toggleAppFlag = apiCall<
  { flagName: string; isOn: boolean },
  boolean
>("update-org-settings", async (p: { flagName: string; isOn: boolean }) => {
  await axios.put("/extproxy/integrations/ui-settings", {
    uiSettings: {
      [p.flagName]: p.isOn,
    },
  });
  return {
    success: true,
    value: true,
  };
});

export const generateKey = apiCall<string, string>(
  "investigations.update",
  async (service) => {
    const res = await axios.post(`/extproxy/integrations/${service}/keys`);
    return {
      success: true,
      value: res.data.apiKey,
    };
  }
);
